'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function BurunDolgusuIleAmeliyatsizBurunDuzeltmePage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/burun-dolgusu-ile-ameliyatsiz-burun-duzeltme';
  const pageTitle = 'Burun Dolgusu ile Ameliyatsız Burun Düzeltme';
  const pageDescription = 'Burun dolgusu ile ameliyatsız burun estetiği nasıl yapılır? Kemer, çöküklük, asimetri ve burun ucu düşüklüğü dolgu ile düzeltilebilir mi? Tüm detaylar burada.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Burun Dolgusu ile Ameliyatsız Burun Düzeltme', href: '/tr/blog/burun-dolgusu-ile-ameliyatsiz-burun-duzeltme' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Burun dolgusu ile ameliyatsız burun estetiği ve liquid rhinoplasty değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-indigo-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Liquid Rhinoplasty
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Burun Dolgusu ile Ameliyatsız Burun Düzeltme
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>4 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>6 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Burun, yüz estetiğinde denge ve oranı belirleyen en önemli bölgedir. Klasik cerrahi rinoplasti her ne kadar kalıcı çözümler sunsa da, ameliyatsız yöntemler arasında burun dolgusu (non-surgical rhinoplasty) son yıllarda en çok tercih edilen uygulamalardan biridir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Burun dolgusu, cerrahi kesi olmadan, sadece birkaç dakikalık enjeksiyon işlemiyle burundaki küçük şekil bozukluklarını düzeltir, daha simetrik ve estetik bir görünüm kazandırır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Burun Dolgusu Nedir?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Burun dolgusu, genellikle hyaluronik asit içeren dolgu maddelerinin cilt altına enjekte edilmesiyle yapılan bir işlemdir. Amaç; burun sırtındaki çöküklükleri, kemer çıkıntılarını veya burun ucu düşüklüğünü dengelemektir.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <p className="text-blue-800 font-medium">
                  💡 Avantajı: Ameliyatsız, ağrısız ve geri dönüşümlü bir işlemdir. Kişi işlem sonrası hemen sosyal yaşamına dönebilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Hangi Durumlarda Uygulanır?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Burun dolgusu aşağıdaki durumlarda etkili bir çözümdür:
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>✅ Burun kemerinin yumuşatılması</li>
                  <li>✅ Burun sırtındaki çöküklüklerin doldurulması</li>
                  <li>✅ Burun ucu düşüklüğünün hafifçe kaldırılması</li>
                  <li>✅ Burun asimetrisinin dengelenmesi</li>
                  <li>✅ Ameliyat sonrası küçük düzensizliklerin düzeltilmesi</li>
                </ul>
                <p className="text-green-800 mt-3 font-medium">
                  Ancak solunum problemi veya büyük yapısal deformiteler varsa cerrahi müdahale (rinoplasti) gerekebilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Burun Dolgusu Nasıl Yapılır?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Bölge antiseptik solüsyonla temizlenir.
              </p>

              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <ol className="list-decimal list-inside text-purple-800 space-y-2">
                  <li>Bölge antiseptik solüsyonla temizlenir</li>
                  <li>Gerekirse lokal anestezik krem uygulanır</li>
                  <li>Dolgu, çok ince iğne veya kanül yardımıyla burun sırtı, uç veya yan bölgelere enjekte edilir</li>
                  <li>İşlem ortalama 10–15 dakika sürer</li>
                </ol>
                <div className="mt-4 space-y-2">
                  <p className="text-purple-800 font-medium">🕒 Etki hemen görülür, ödem birkaç gün içinde azalır</p>
                  <p className="text-purple-800 font-medium">💎 Kalıcılık süresi kullanılan dolgu tipine göre 12–18 ay arasındadır</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Ameliyatsız Burun Düzeltme ile Cerrahi Arasındaki Fark
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Özellik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Burun Dolgusu
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Cerrahi Rinoplasti
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Yöntem</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Enjeksiyon</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Cerrahi işlem (kesili)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Süre</td>
                      <td className="px-6 py-4 text-sm text-gray-900">10–15 dakika</td>
                      <td className="px-6 py-4 text-sm text-gray-900">1–3 saat</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Anestezi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Krem veya lokal</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Genel anestezi</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">İyileşme</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hemen sosyal hayata dönüş</td>
                      <td className="px-6 py-4 text-sm text-gray-900">7–10 gün istirahat</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kalıcılık</td>
                      <td className="px-6 py-4 text-sm text-gray-900">12–18 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kalıcı</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Risk</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Düşük</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Orta-yüksek (kanama, ödem)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-yellow-50 rounded-xl p-6 mb-8">
                <p className="text-yellow-800 font-medium">
                  💡 Burun dolgusu, ameliyatsız alternatif isteyen hastalar için ideal; cerrahiye uygun olmayan hafif deformitelerde mükemmel sonuçlar sağlar.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Burun Dolgusunun Avantajları
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>✅ Cerrahi kesi veya dikiş gerektirmez</li>
                  <li>✅ Anında sonuç verir</li>
                  <li>✅ Simetri ve dengeyi düzeltir</li>
                  <li>✅ İstenirse geri döndürülebilir (hyaluronidaz enzimi ile)</li>
                  <li>✅ Doğal ve orantılı bir görünüm sağlar</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Burun Dolgusu Kimler İçin Uygundur?
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-green-900 mb-3">Uygun Adaylar:</h3>
                  <ul className="list-disc list-inside text-green-800 space-y-2">
                    <li>Cerrahi müdahaleden çekinen kişiler</li>
                    <li>Küçük kemer veya çöküklüğü olan hastalar</li>
                    <li>Burun ucu düşük veya asimetrik kişiler</li>
                    <li>Rinoplasti sonrası minör düzensizlikleri olanlar</li>
                  </ul>
                </div>

                <div className="bg-red-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-red-900 mb-3">⚠️ Uygun değildir:</h3>
                  <ul className="list-disc list-inside text-red-800 space-y-2">
                    <li>Gebe ve emziren kadınlar</li>
                    <li>Aktif cilt enfeksiyonu bulunanlar</li>
                    <li>Kan sulandırıcı ilaç kullananlar</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7. Dikkat Edilmesi Gerekenler
              </h2>
              
              <div className="bg-orange-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside text-orange-800 space-y-2">
                  <li>İşlem sonrası 24 saat makyaj yapılmamalı</li>
                  <li>48 saat sauna, hamam, spor önerilmez</li>
                  <li>İlk gün buruna bastırılmamalı, gözlük kullanılmamalıdır</li>
                  <li>Bol su içmek, dolgunun stabilitesini destekler</li>
                </ul>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Burun dolgusu ile ameliyatsız burun estetiği ve liquid rhinoplasty"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Burun dolgusu kalıcı mı?</h3>
                  <p className="text-gray-700">Hayır, kullanılan dolgu maddesi zamanla vücut tarafından emilir; ortalama 12–18 ay etkilidir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Dolgu sonrası şişlik olur mu?</h3>
                  <p className="text-gray-700">Evet, hafif ödem ve hassasiyet normaldir; 1–2 gün içinde azalır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Dolgu burnu büyütür mü?</h3>
                  <p className="text-gray-700">Hayır. Ama orantısal olarak daha dengeli bir görünüm sağladığı için burun küçülmüş gibi görünür.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Cerrahi rinoplasti sonrası dolgu yapılabilir mi?</h3>
                  <p className="text-gray-700">Evet, küçük asimetrileri düzeltmek için sıklıkla kullanılır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Dolgu istenmezse geri alınabilir mi?</h3>
                  <p className="text-gray-700">Evet. Hyaluronidaz enzimi ile dolgu tamamen çözülebilir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>American Society of Plastic Surgeons (ASPS) – "Non-Surgical Rhinoplasty Overview"</li>
                <li>Türk Estetik Tıp Derneği (TETD) – Burun Dolgusu Uygulama Kılavuzu</li>
                <li>Harvard Health Publishing – "Liquid Rhinoplasty: What to Expect"</li>
                <li>Mayo Clinic – "Hyaluronic Acid Fillers and Facial Symmetry"</li>
                <li>ISAPS Global Report 2024 – "Injectable Trends in Non-Surgical Nose Reshaping"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#burundolgusu</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#ameliyatsızburunestetiği</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#liquidrhinoplasty</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#hyaluronikasit</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#burunucu</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#estetik</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Burun Dolgusu İçin Randevu Alın</h3>
              <p className="mb-6">Ameliyatsız burun estetiği ve liquid rhinoplasty için uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Burun dolgusu hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-indigo-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/alin-bolgesine-dolgu-uygulamasi-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Alın Bölgesine Dolgu Uygulaması</h4>
              <p className="text-gray-600 text-sm">Alın dolgusu kırışıklıkları azaltır, yüz hatlarını dengeleyip genç görünüm sağlar.</p>
            </Link>
            <Link
              href="/tr/blog/dolgu-islemleri-yuz-liftingi-saglar-mi"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dolgu İşlemleri Yüz Liftingi Sağlar mı?</h4>
              <p className="text-gray-600 text-sm">Cerrahi olmayan yüz gençleştirmenin bilimsel açıklaması.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
